/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";
import { RulesContext } from "../RulesContext";
import { useNavigate, useBlocker } from "react-router";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import AppSettings from "../../../../Settings/AppSettings";
import Utils from "../../../../Common/Utils";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../../Context/AppContext";
/*
  DO NOT REMOVE THIS IMPORT (§ 112).

  This component no longer uses a single class from `Rules.css` — every label it
  styled is now a `FieldTitle`. The import stays because `RuleHeader.js` is the
  **only** file in the repository that imports this stylesheet, and
  `.text-caption-style` is defined **nowhere else**: not in `styles/styles.css`,
  not in `BuilderStyle.css`, which between them carry a duplicate of every OTHER
  class in this file. Ten files under `Pages/GMS/ABTesting/` use
  `.text-caption-style` and are styled solely because this line runs.

  Verified 2026-08-27 by scanning all 15 stylesheets under `src/` for each of
  `Rules.css`'s 16 selectors. Dropping the import — or lazy-loading this route —
  silently unstyles ten unrelated screens. The fix is to move
  `.text-caption-style` into `styles/main.css` beside the other global type
  utilities and then delete this file; that is a separate change with its own
  blast radius, tracked in `okf/rules/e2e.md`.
*/
import "../../Rules/Rules.css";
import { STATUS_LOOKUP, ASYMMETRIC, ASYMMETRIC_INSTRUCTIONS, CBA_INSTRUCTIONS, CBA_RULE, CBI_INSTRUCTIONS, CBI_RULE, CBM_INSTRUCTIONS, CBM_RULE, COMMITT_IN_PROGRESS_MESSAGE, RULE_DESCRIPTION, RULE_NAME, RULE_NAME_INSTRUCTIONS, RULE_TYPE, SELECT_RULE_TYPE, DECOMMITTED_MESSAGE, COMMITTED_MESSAGE, DECOMMITT_IN_PROGRESS_MESSAGE, CHANGE_RULE_WARNING } from "./RuleConstants";
import { RuleContext } from "Pages/GMS/Rules/contexts/RuleContext";
import DecommitConfirmModal from "Pages/GMS/ISR/Components/RuleComponents/DecommitConfirmModal";
import _isEqual from "lodash/isEqual";
import ReloadButton from "Pages/GMS/ARS/Components/ReloadButton";
import Button from "Components/DesignSystem/Button/Button";
import TextField from "Components/DesignSystem/TextField/TextField";
import Textarea from "Components/DesignSystem/Textarea/Textarea";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import Typography from "Components/DesignSystem/Typography/Typography";
import Status from "Components/DesignSystem/Status/Status";
import Tag from "Components/DesignSystem/Tag/Tag";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Alert from "Components/DesignSystem/Alert/Alert";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import RuleChangeConfirmDialog from "./RuleChangeConfirmDialog";
import { ChevronLeft } from "Components/DesignSystem/Iconography/Icons";

const RuleHeader = ({ ruleDetails, editPage, load_Rule_Details, loading, type, setRuleDetails, ruleHeaderDetails, setRuleHeaderDetails, fromCategoryRelevancy = false, onClose, onSaved }) => {
  const navigate = useNavigate();
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const appData = useContext(AppContext);
  const rulesContextData = useContext(RulesContext);
  const { setRuleType, ruleType, cellStates, selectedCategories, ruleState, setLoading, industryId, CBMOptions, setCbmRuleId, setCompatibilityData, ruleDispatch, initialRuleDetails, setInitialRuleDetails, setCBMOptions, allowNavigationRef } = rulesContextData;
  const { leftCatListOption, rightCatListOption, leftValuesAttr, rightValuesAttr, defaultCompatibilityValue, compatibilityLevel, leftValuesAttrName, rightValuesAttrName, leftAttributeValueId, rightAttributeValueId, rightAttributeValueName, leftAttributeValueName, isProductFilter, } = CBMOptions;

  const [intialData, setIntialData] = useState(null);
  const [disableButton, setDisableButton] = useState({ commit: false, decommit: false, save: false, });
  const [edgeInfo, setEdgeInfo] = useState({ count: 0, status: "", message: "", });
  const [ruleStatus, setRuleStatus] = useState({ action: "", status: "", message: "", });

  const [showDecommitModal, setShowDecommitModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  /* The rule type the user picked but has not confirmed yet (§ 113). `null` means no
     confirm is pending — `''` is not a possible value, so `null` is unambiguous.
     Bootbox held this in its own closure; a React dialog needs it in state. */
  const [pendingRuleType, setPendingRuleType] = useState(null);
  const hasUnsavedChangesRef = useRef(false);
  // The sidebar save handler below is registered once per [ruleDetails, type], so calling
  // `saveRule` from it directly used the form state of the render that registered it, i.e.
  // before the user's edits: a new rule saved with no name, an existing one dropped its edits.
  // It reads the latest `saveRule` through this ref instead (found testing AB#15859).
  const saveRuleRef = useRef(null);

  useEffect(() => {
    const isValidMode = type === "edit" || type === "view";
    const hasRuleId = Boolean(ruleDetails?.id);

    if (isValidMode && hasRuleId) {
      getRuleStatus(true);
      getEdgeCount();
    }
    if (type !== "view") {
      appData?.setEditInProgress(true);
      // The sidebar's "Unsaved Changes" dialog (Templates/MenuItem.tsx) runs these and then
      // navigates. That navigation must pass this editor's own useBlocker, or the editor asks
      // the same question a second time (AB#15859). Both handlers therefore allow it.
      // `openSavedRule: false`: the user is leaving, so a new rule must not open its editor
      // first (AB#15860).
      appData?.setEditSaveHandler(() => async () => {
        const success = await saveRuleRef.current({ openSavedRule: false });
        if (success) allowNavigationRef.current = true;
        return success ?? false;
      });
      appData?.setEditCancelHandler(() => () => { allowNavigationRef.current = true; });
    }

    if (intialData === null) {
      setIntialData(ruleDetails);
    }
    return () => {
      if (type !== "view") {
        appData?.setEditInProgress(false);
        appData?.setEditSaveHandler(null);
        appData?.setEditCancelHandler(null);
      }
    }
  }, [ruleDetails, type]);

  useEffect(() => {
    if (`${ruleStatus?.action}_${ruleStatus?.status}` !== "") {
      switch (
      STATUS_LOOKUP[`${ruleStatus?.action}_${ruleStatus?.status}`]?.message
      ) {
        case COMMITT_IN_PROGRESS_MESSAGE:
          setDisableButton({ commit: true, decommit: true, save: true });
          break;
        case DECOMMITT_IN_PROGRESS_MESSAGE:
          setDisableButton({ commit: true, decommit: true, save: true });
          break;
        case COMMITTED_MESSAGE:
          setDisableButton({ commit: true, decommit: false, save: true });
          break;
        case DECOMMITTED_MESSAGE:
          setDisableButton({ commit: !isRuleCommitable(), decommit: true, save: true });
          break;
        default:
          setDisableButton({ commit: false, decommit: false, save: false });
          break;
      }
    }
  }, [ruleStatus]);

  // Define these variables outside of any effect or function to make them accessible component-wide
  const isCBICBACommitable = () => {
    return !Utils.isEmpty(ruleHeaderDetails.name) &&
      !Utils.isEmpty(ruleHeaderDetails.rule_type) &&
      !Utils.isEmpty(selectedCategories.leftCategory.id) &&
      !Utils.isEmpty(selectedCategories.rightCategory.id) &&
      ruleState.rule.length > 0 &&
      !processRunning;
  };

  const isCBMCommitable = () => {
    // Check basic requirements
    if (Utils.isEmpty(ruleHeaderDetails.name) ||
      Utils.isEmpty(ruleHeaderDetails.rule_type) ||
      Utils.isEmpty(selectedCategories.leftCategory.id) ||
      Utils.isEmpty(selectedCategories.rightCategory.id) ||
      processRunning) {
      return false;
    }

    // Check for valid CBM attributes based on type
    if (!leftCatListOption || !rightCatListOption) {
      return false;
    }

    // For "products" type
    if (leftCatListOption === "products") {
      if (isProductFilter &&
        (!leftValuesAttr || !rightValuesAttr || !leftValuesAttrName || !rightValuesAttrName ||
          !leftAttributeValueId || !rightAttributeValueId ||
          !leftAttributeValueName || !rightAttributeValueName)) {
        return false;
      }
    }
    // For "values" type
    else if (leftCatListOption === "values") {
      if (!leftValuesAttr || !rightValuesAttr ||
        !leftValuesAttrName || !rightValuesAttrName) {
        return false;
      }
    }

    // Check if CBM has valid matrix rules
    return Object.values(cellStates).some(value => value !== 0);
  };


  const isRuleCommitable = () => {
    const { rule_type } = ruleHeaderDetails;

    if (rule_type === CBM_RULE) {
      return isCBMCommitable();
    } else if (rule_type === CBI_RULE || rule_type === CBA_RULE) {
      return isCBICBACommitable();
    }

    return false;
  };

  const isViewMode = type === "view";
  const [processRunning, setProcessRunning] = useState(false);
  const { setNewId } = useContext(RuleContext);
  const [commit, setCommit] = useState(false);

  useEffect(() => {
    if (ruleDetails) {
      const { name, rule_type, rule_status, description, commit, asymmetric } = ruleDetails;
      let asymmetric_val = typeof asymmetric === "boolean" ? asymmetric : false;

      setRuleHeaderDetails({
        ...ruleHeaderDetails,
        name,
        rule_type,
        rule_status,
        description,
        asymmetric: asymmetric_val,
      });
      setCommit(commit);
    }
    if (!processRunning) {
      setDisableButton({ decommit: false, commit: false, save: false });
    }
  }, [ruleDetails]);

  useEffect(() => {
    const { name, rule_type } = ruleHeaderDetails;
    const { leftCategory, rightCategory } = selectedCategories;

    // Helper function to check if CBM has valid matrix rules
    const hasCBMValidRules = () => {
      return Object.values(cellStates).some(value => value !== 0);
    };

    // Helper function to check if CBM attributes are properly set
    const isCBMAttributesValid = () => {
      // Common validation: left and right CatListOption must be set
      if (!leftCatListOption || !rightCatListOption) {
        return false;
      }

      // For "products" type
      if (leftCatListOption === "products") {
        // Basic validation for products
        if (!isProductFilter) return true;

        // If product filter is enabled, check additional fields
        if (!leftValuesAttr || !rightValuesAttr || !leftValuesAttrName || !rightValuesAttrName ||
          !leftAttributeValueId || !rightAttributeValueId ||
          !leftAttributeValueName || !rightAttributeValueName) {
          return false;
        }
      }
      // For "values" type
      else if (leftCatListOption === "values") {
        if (!leftValuesAttr || !rightValuesAttr ||
          !leftValuesAttrName || !rightValuesAttrName) {
          return false;
        }
      }

      return true;
    };

    // Helper function to check if CBI/CBA rules are valid
    const isCBICBAValid = () => {
      return !Utils.isEmpty(name) &&
        !Utils.isEmpty(rule_type) &&
        !Utils.isEmpty(leftCategory.id) &&
        !Utils.isEmpty(rightCategory.id) &&
        ruleState.rule.length > 0;
    };

    // For committed rules
    if (editPage && ruleDetails?.commit) {
      // For committed rule - Save always disabled, Decommit always enabled, Commit initially disabled
      setDisableButton({ commit: true, decommit: false, save: true });

      // For committed rule, enable Commit if changes detected AND the rule is valid
      const currentState = JSON.parse(JSON.stringify(composeRuleChanges()));
      if (hasStateChanged(initialRuleDetails, currentState)) {
        // Only enable Commit if the rule is valid
        if (rule_type === CBM_RULE) {
          // For CBM rules, validate CBM attributes and rules
          if (isCBMAttributesValid() && hasCBMValidRules()) {
            setDisableButton(prev => ({ ...prev, commit: false }));
          }
        } else if ((rule_type === CBI_RULE || rule_type === CBA_RULE) && isCBICBAValid()) {
          // For CBI/CBA rules, validate rule conditions
          setDisableButton(prev => ({ ...prev, commit: false }));
        }
      }
    }
    // For decommitted (existing) rules
    else if (editPage && !ruleDetails?.commit) {
      // For decommitted rule, Decommit always disabled
      setDisableButton({ commit: true, decommit: true, save: true });

      // Check if there are changes to the rule
      const currentState = JSON.parse(JSON.stringify(composeRuleChanges()));
      const hasChanges = hasStateChanged(initialRuleDetails, currentState);

      // For decommitted rule, check if Save should be enabled after user made changes
      if (!processRunning && hasChanges) {
        // For CBI/CBA rules
        if ((rule_type === CBI_RULE || rule_type === CBA_RULE) && isCBICBAValid()) {
          setDisableButton(prev => ({ ...prev, save: false }));
        }
        // For CBM rules
        else if (rule_type === CBM_RULE && isCBMAttributesValid()) {
          setDisableButton(prev => ({ ...prev, save: false }));
        }
      }

      // For decommitted rule, check if Commit should be enabled
      if (!processRunning) {
        // For CBI/CBA rules
        if ((rule_type === CBI_RULE || rule_type === CBA_RULE) && isCBICBAValid()) {
          setDisableButton(prev => ({ ...prev, commit: false }));
        }
        // For CBM rules
        else if (rule_type === CBM_RULE && isCBMAttributesValid() && hasCBMValidRules()) {
          setDisableButton(prev => ({ ...prev, commit: false }));
        }
      }
    }
    // For new rules (not edit page)
    else if (!editPage) {
      // Reset buttons for new rule
      setDisableButton({ commit: true, decommit: true, save: true });

      // Conditions to enable Save for new rule
      if (!processRunning) {
        // For CBI/CBA rules
        if ((rule_type === CBI_RULE || rule_type === CBA_RULE) && isCBICBAValid()) {
          setDisableButton(prev => ({ ...prev, save: false }));
        }
        // For CBM rules
        else if (rule_type === CBM_RULE && !Utils.isEmpty(name) &&
          !Utils.isEmpty(leftCategory.id) && !Utils.isEmpty(rightCategory.id) &&
          isCBMAttributesValid()) {
          setDisableButton(prev => ({ ...prev, save: false }));
        }
      }
    }

    setRuleType(rule_type);
  }, [ruleHeaderDetails, rulesContextData, selectedCategories, ruleState, cellStates]);
  function handle_change(id, value) {

    if (id === "rule_type") {
      setCompatibilityData([]);
      ruleDispatch({ type: "CBM_RULE_TYPE", data: [] });
      setCBMOptions({ leftCatListOption: "", rightCatListOption: "", leftCatAttributeSet: [], rightCatAttributeSet: [], leftCatProductList: [], rightCatProductList: [], leftAttributeValues: [], rightAttributeValues: [], leftValuesAttr: "", leftValuesAttrName: "", rightValuesAttr: "", rightValuesAttrName: "", leftAttributeValueName: '', leftAttributeValueId: '', rightAttributeValueName: '', rightAttributeValueId: '', defaultCompatibilityValue: "", compatibilityLevel: "level4", showWarningModal: false, editMode: false, toggleSelectInput: false, showWarninModal: false, leftCatID: "", rightCatID: "", selectedCell: {}, valueToBeSaved: "", isProductFilter: false });
      setRuleDetails((prev) => {
        return {
          ...prev,
          ...ruleHeaderDetails,
          rule_body: {},
          rule_type: value,
        };
      });
    } else {
      setRuleHeaderDetails((prevState) => {
        return { ...prevState, [id]: value };
      });
    }
  }

  function hasStateChanged(initialState, changeState) {
    const keysToCheck = [
      "name",
      "rule_type",
      "description",
      "asymmetric",
      "left_category",
      "right_category",
    ];

    if (initialState && changeState) {
      for (const key of keysToCheck) {
        if (!_isEqual(initialState[key], changeState[key])) {
          return true;
        }
      }

      if (initialState.rule_type === "CBM" && changeState.rule_type === "CBM") {
        const initialCellStates = initialState.rule_body?.cellStates || {};
        const changeCellStates = changeState.rule_body?.cellStates || {};

        const initialEntries = Object.entries(initialCellStates)
          .filter(([_, value]) => value !== 0);
        const changeEntries = Object.entries(changeCellStates)
          .filter(([_, value]) => value !== 0);

        if (!_isEqual(initialEntries, changeEntries)) {
          return true;
        }

        const cbmPropsToCheck = [
          "defaultCompatibilityValue",
          "compatibilityLevel",
          "leftCatCompatibilityType",
          "rightCatCompatibilityType",
          "isProductFilter"
        ];

        for (const prop of cbmPropsToCheck) {
          if (!_isEqual(initialState.rule_body?.[prop], changeState.rule_body?.[prop])) {
            return true;
          }
        }

        if (changeState.rule_body?.isProductFilter) {
          const productPropsToCheck = [
            "leftAttributeId",
            "rightAttributeId",
            "leftValuesAttrName",
            "rightValuesAttrName",
            "leftAttributeValueId",
            "rightAttributeValueId",
            "leftAttributeValueName",
            "rightAttributeValueName"
          ];

          for (const prop of productPropsToCheck) {
            if (!_isEqual(initialState.rule_body?.[prop], changeState.rule_body?.[prop])) {
              return true;
            }
          }
        }

        return false;
      }
      else if (!_isEqual(initialState.rule_body, changeState.rule_body)) {
        return true;
      }
    }
    return false;
  }

  const handleCommit = async () => {
    try {
      setEdgeInfo({ status: "Loading", message: "Loading", count: null });
      saveRule(false).then(async (success) => {
        if (success) {
          setRuleStatus({ action: "Commit", status: "In Progress" });
          setDisableButton({ save: true, commit: true, decommit: true });
          setProcessRunning(true);
          setLoading({ status: "Saving", message: "Committing rule" });
          const res = await ServiceWrapper.doPost(
            `${api_base_url}rule/publish/${ruleDetails.id}`,
            null,
            null,
            AuthManager.getGmsToken()
          );
          if (!res.errorfound) {
            setDisableButton({ save: false, commit: true, decommit: false });
            setLoading({ status: "Success", message: "Saved" });
            if (res?.data?.done) {
              /* Was JSX plus `style: { backgroundColor: "#e3fcef" }` — a hand-set
                 green that the DS `success` tone already paints, and a wrapper
                 whose only job was to apply the semibold the toast title carries
                 by default (§ 116). */
              toast.success("Committing in progress");
              setEdgeInfo({ status: "", message: undefined, count: "0" });
              setTimeout(() => {
                getRuleStatus();
                getEdgeCount();
                load_Rule_Details(ruleDetails.id);
              }, 2000);
            } else {
              toast.error("Error occurred while adding rule to queue.");
              setEdgeInfo({ status: "", message: undefined, count: "0" });
            }
          } else {
            setDisableButton({ save: false, commit: false, decommit: true });
            setProcessRunning(false);
            toast.error("Error while committing rule");
            setLoading({
              status: "Error",
              message: "Error while commiting Rules",
            });
          }
        }
      });
      setTimeout(() => {
        if (fromCategoryRelevancy) {
          onClose?.();
        } else {
          allowNavigationRef.current = true;
          navigate("/gms/compatibilities");
        }
      }, 3000);
    } catch (error) {
      console.log("consthandle_commit= ~ error:", error);
    }
  };

  const onHandleDecommit = () => {
    setShowDecommitModal(true);
  };

  const onDecommitConfirm = () => {
    setShowDecommitModal(false);
    handleDecommit();
  };

  const composeRuleChanges = (json_string = false) => {
    let finalRule;
    if (ruleType === CBM_RULE) {
      finalRule = {
        ...ruleHeaderDetails,
        left_category: selectedCategories.leftCategory.id,
        right_category: selectedCategories.rightCategory.id,
        commit: commit,
        industry_id: industryId,
        rule_body: {
          defaultCompatibilityValue: defaultCompatibilityValue,
          compatibilityLevel: compatibilityLevel,
          leftCatCompatibilityType: leftCatListOption,
          rightCatCompatibilityType: rightCatListOption,
          leftCatID: selectedCategories.leftCategory.id,
          rightCatID: selectedCategories.rightCategory.id,
          cellStates: cellStates,
          leftAttributeId: leftValuesAttr,
          leftValuesAttrName: leftValuesAttrName,
          rightAttributeId: rightValuesAttr,
          isProductFilter: isProductFilter,
          rightValuesAttrName: rightValuesAttrName,
          ...(leftCatListOption === "products" && {
            leftAttributeValueId: leftAttributeValueId,
            leftAttributeValueName: leftAttributeValueName,
            rightAttributeValueId: rightAttributeValueId,
            rightAttributeValueName: rightAttributeValueName,
          }),
          rule: {
            rule: ruleState.rule,
          },
        },
      };
    } else {
      finalRule = {
        ...ruleHeaderDetails,
        industry_id: industryId,
        left_category: selectedCategories.leftCategory.id,
        right_category: selectedCategories.rightCategory.id,
        commit: commit,
        rule_body: {
          leftCatID: selectedCategories.leftCategory.id,
          rightCatID: selectedCategories.rightCategory.id,
          rule: {
            rule: ruleState.rule,
          },
        },
      };
    }
    if (json_string) finalRule.rule_body = JSON.stringify(finalRule?.rule_body);
    return finalRule;
  };

  // Computed: are there unsaved changes right now?
  const hasUnsavedChanges = !isViewMode && !ruleDetails?.commit && (() => {
    if (!editPage) {
      // Create mode: flag as dirty if user has entered any data
      return !!(ruleHeaderDetails.name || ruleHeaderDetails.rule_type || ruleHeaderDetails.description);
    }
    // Edit mode: compare current state against what was loaded
    return initialRuleDetails != null && hasStateChanged(initialRuleDetails, composeRuleChanges());
  })();

  // Keep ref in sync so event listeners always see the latest value
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Listen for top-header dropdown clicks while editInProgress
  useEffect(() => {
    const handleUnsavedAttempt = () => {
      if (hasUnsavedChangesRef.current) {
        setShowSaveModal(true);
      }
    };
    window.addEventListener('unsaved-changes-attempt', handleUnsavedAttempt);
    return () => window.removeEventListener('unsaved-changes-attempt', handleUnsavedAttempt);
  }, []);

  // Intercept React Router in-app navigation (browser back/forward, Links,
  // programmatic navigate() elsewhere in the app)
  const blocker = useBlocker(() => {
    if (allowNavigationRef.current) return false;
    return hasUnsavedChanges;
  });

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowSaveModal(true);
    }
  }, [blocker.state]);

  // Warn on browser tab/window close
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const onBackButtonClicked = () => {
    if (!isViewMode && !ruleDetails?.commit) {
      if (hasStateChanged(initialRuleDetails, composeRuleChanges()))
        return setShowSaveModal(true);
    }
    if (fromCategoryRelevancy) return onClose?.();
    return navigate("/gms/compatibilities");
  };

  const onSaveConfirm = async () => {
    setShowSaveModal(false);
    // Leave only once the save succeeded; a failed save used to navigate away anyway and lose
    // the changes. A new rule stays on this mount so the navigation below still runs here
    // (AB#15860).
    const saved = await saveRule({ openSavedRule: false });
    if (!saved) return;
    allowNavigationRef.current = true;
    setTimeout(() => {
      if (fromCategoryRelevancy) {
        onClose?.();
      } else if (blocker.state === "blocked") {
        blocker.proceed();
      } else {
        navigate("/gms/compatibilities");
      }
    }, 3000);
  };

  const onDiscardChanges = () => {
    setShowSaveModal(false);
    allowNavigationRef.current = true;
    if (fromCategoryRelevancy) {
      onClose?.();
    } else if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      navigate("/gms/compatibilities");
    }
  };

  const handleDecommit = async () => {
    try {
      setEdgeInfo({ status: "Loading", message: "Loading", count: null });
      saveRule(false).then(async (success) => {
        if (success) {
          setRuleStatus({ action: "Decommit", status: "In Progress" });
          setDisableButton({ save: true, commit: true, decommit: true });
          setProcessRunning(true);
          setLoading({ status: "Saving", message: "Decommiting rule" });
          const res = await ServiceWrapper.doPost(
            `${api_base_url}rule/decommit/${ruleDetails.id}`,
            null,
            null,
            AuthManager.getGmsToken()
          );
          if (res.errorfound) {
            toast.error("Error while decommiting rule");
            updateHeader();
            setEdgeInfo({ status: "Success", message: undefined, count: "0" });
            setDisableButton({ save: false, commit: true, decommit: false });
            return;
          }
          if (res?.data?.done) {
              /* Was JSX plus `style: { backgroundColor: "#e3fcef" }` — a hand-set
               green that the DS `success` tone already paints, and a wrapper
               whose only job was to apply the semibold the toast title carries
               by default (§ 116). */
            toast.success("Decommitting in progress");
            if (fromCategoryRelevancy) {
              onClose?.();
            } else {
              allowNavigationRef.current = true;
              navigate("/gms/compatibilities");
            }
          } else {
            toast.error("Error occurred while adding rule to queue.");
          }
          setLoading({ status: "Success", message: "Saved" });
          setEdgeInfo({ status: "", message: undefined, count: "0" });
          updateHeader();
          load_Rule_Details(ruleDetails.id);
        }
      });
    } catch (error) {
      console.log("consthandle_decommit= ~ error:", error);
    }
  };

  useEffect(() => {
    updateHeader();
  }, [ruleDetails?.id]);

  const updateHeader = () => {
    setProcessRunning(false);
  };

  const checkDownloadStatus = async (jobid) => {
    try {
      const statusResponse = await ServiceWrapper.doGet(
        `${api_base_url}job/status/${jobid}`,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (!statusResponse.errorfound) {
        const { data } = statusResponse;

        if (data?.error) {
          setEdgeInfo({ status: "", message: "", count: null });
          return;
        }

        if (data?.processing) {
          setTimeout(() => {
            checkDownloadStatus(jobid);
          }, 2000);
          setEdgeInfo({
            status: "Loading",
            message: statusResponse?.data?.message,
            count: null,
          });
          return;
        }
        setProcessRunning(false);
        const edgeCount = data.data;
        setEdgeInfo({
          status: "Success",
          message: "Completed",
          count: edgeCount,
        });
      }
    } catch (error) {
      console.error("Error occurred while checking download status:", error);
    }
  };

  const getRuleStatus = async (firstLoad = false) => {
    try {
      const ret = await ServiceWrapper.doGet(
        `${api_base_url}rule/status/${ruleDetails?.id}`,
        null,
        null,
        AuthManager.getGmsToken()
      );
      if (!ret.errorfound) {
        setRuleStatus(ret?.data?.rule_status);
        if (ret?.data?.rule_status?.status === "Failed") {
          !firstLoad &&
            toast.error(
              `${ret?.data?.rule_status?.action} ${ret?.data?.rule_status?.status} retry again`
            );
          if (ret?.data?.rule_status?.action === "Commit") {
            setDisableButton({ decommit: true, commit: false, save: true });
          } else
            setDisableButton({ decommit: false, commit: true, save: true });
        }
      } else {
        toast.error("Error while fetching rule status");
      }
    } catch (error) {
      console.log("getRuleStatus ~ error:", error);
    }
  };

  const getEdgeCount = async () => {
    try {
      setEdgeInfo({
        ...edgeInfo,
        status: "Loading",
        message: "Preparing edge count request...",
      });

      const ret = await ServiceWrapper.doGet(
        `${api_base_url}rule/edge-count/${ruleDetails?.id}`,
        null,
        null,
        AuthManager.getGmsToken()
      );
      if (!ret.errorfound) {
        setTimeout(() => {
          checkDownloadStatus(ret.data.jobid);
        }, 2000);
      } else {
        toast.error("Error while fetching Edge count");
        setEdgeInfo({
          status: "Error",
          message: "Error while fetching Edge count",
        });
      }
    } catch (error) {
      console.log("getEdgeCount ~ error:", error);
    }
  };

  // `openSavedRule`: after a NEW rule is created, open its editor (/gms/rule/edit/<id>). Callers
  // that leave the page right after saving pass false, because that route is keyed on the rule
  // id (App.tsx) and REMOUNTS the editor: their pending navigation would then run against the
  // new mount's blocker, and the page stayed on the new rule (AB#15860).
  const saveRule = async ({ reload = true, openSavedRule = true } = {}) => {
    try {
      setDisableButton({ decommit: true, commit: true, save: true });
      setProcessRunning(true);
      let finalRule = composeRuleChanges(true);
      setLoading({ status: "Saving", message: "Saving Rules details" });
      let url;
      let res;
      if (editPage) {
        url = `${api_base_url}rule/edit/${ruleDetails.id}`;
        res = await ServiceWrapper.doPut(
          url,
          finalRule,
          null,
          AuthManager.getGmsToken()
        );
      } else {
        url = `${api_base_url}rule/create`;
        res = await ServiceWrapper.doPost(
          url,
          finalRule,
          null,
          AuthManager.getGmsToken()
        );
      }
      setProcessRunning(false);

      if (res.data.done) {
        if (!editPage) {
          toast.success("Saved successfully");
          setNewId(res?.data?.id);
          if (fromCategoryRelevancy) {
            onSaved?.(res?.data?.id);
          } else if (openSavedRule) {
            allowNavigationRef.current = true;
            navigate(`/gms/rule/edit/${res?.data?.id ?? "0"}`, { replace: true });
          }
        } else {
          toast.success("Updated successfully");
          load_Rule_Details(res?.data?.id);
          setDisableButton(prev => ({ ...prev, save: true }));
        }
        setLoading({ status: "Success", message: res.data.message });
        setCbmRuleId(res.data.id);
        return true;
      } else {
        toast.error(res.data.message);
        return false;
      }
    } catch (error) {
      console.log("Save_Rule_Details ~ error:", error);
    }
  };
  saveRuleRef.current = saveRule;

  /**
   * `Dropdown.onChange` hands over the VALUE alone where the `<select>` handed over an
   * event; `e.target.id` was the literal `"rule_type"` — the `<select>`'s own id — so
   * nothing about the branch below changes.
   *
   * What DID change (§ 113): the confirm is a React dialog instead of
   * `Bootbox.confirm("Change Rule Type?", …)`, so the chosen value waits in state
   * rather than in a Bootbox closure. The gate is identical — a non-empty
   * `ruleState.rule` means there is rule logic to destroy, so ask first; otherwise
   * apply immediately — and, as with Bootbox, only the confirm button applies it.
   */
  const handleOnRuleTypeChange = (value) => {
    if (ruleState && ruleState.rule.length !== 0) {
      setPendingRuleType(value);
    } else {
      handle_change("rule_type", value);
    }
  };

  const confirmRuleTypeChange = () => {
    handle_change("rule_type", pendingRuleType);
    setPendingRuleType(null);
  };

  /**
   * `Toggle` reports the next checked state directly, so the two branches that both
   * did `setRuleHeaderDetails({ ...prev, asymmetric: <literal> })` collapse into one.
   */
  const handleOnAsymmetricChange = (checked) => {
    setRuleHeaderDetails((prevState) => {
      return { ...prevState, asymmetric: checked };
    });
  };

  /**
   * Status → tone, keyed on the lookup's own MESSAGE rather than on a second copy of
   * its eight composite keys.
   *
   * That works because `STATUS_LOOKUP`'s three colours map onto three messages with
   * no crossover: `#fff0b3` always carries an in-progress message and `loader: true`,
   * `#e3fcef` always carries `COMMITTED_MESSAGE`, `#fce3e3` always carries
   * `DECOMMITTED_MESSAGE`. Keying on the message is therefore total, and it cannot
   * drift out of step with `RuleConstants.ts` the way a duplicated key list would.
   *
   * It also explains the two entries that read like typos and are not:
   * `Commit_Failed` says "Decommitted" and `Decommit_Failed` says "Committed",
   * because a transition that failed leaves the rule in the state it started in.
   */
  const STATUS_TONE = {
    [COMMITTED_MESSAGE]: "success",
    [DECOMMITTED_MESSAGE]: "danger",
    [COMMITT_IN_PROGRESS_MESSAGE]: "warning",
    [DECOMMITT_IN_PROGRESS_MESSAGE]: "warning",
  };

  const statusChip = () => {
    const entry = STATUS_LOOKUP[`${ruleStatus?.action}_${ruleStatus?.status}`];
    /*
      No lookup hit means there is nothing to report yet — say nothing.

      The legacy chip rendered unconditionally, so on every edit-page load, for as
      long as `rule/status` was in flight, it painted a 32px box with
      `backgroundColor: undefined` and an empty label. At `tw-w-[120%]` it also
      overhung its own parent by a fifth of its width while doing it. An empty chip
      is not a state.
    */
    if (!entry) return null;
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-2">
        <Status
          tone={STATUS_TONE[entry.message] ?? "neutral"}
          label={entry.message}
          /* `live` because this ONE chip mutates in place as the commit advances
             (In Queue → In Progress → Completed) without the page reloading —
             exactly the case the prop is defaulted off for elsewhere (a table of
             50 chips would announce 50 times). */
          live
          data-testid="rule-status-chip"
        />
        {entry.loader ? (
          /* Decorative motion beside a label the chip above already announces.
             `Spinner` is a `role="status"` with its label in `sr-only`, so left
             visible to assistive technology it would read the same string a second
             time. The legacy `LoadIcon` was a bare unlabelled SVG; this matches it. */
          <Spinner
            size="sm"
            label={entry.message}
            aria-hidden="true"
            data-testid="rule-status-spinner"
          />
        ) : null}
      </span>
    );
  };

  /* The two dimmed regions in `view` mode, unchanged. `pointer-events: none` is what
     actually makes the form read-only here — there is no `disabled` pass — so these
     objects are load-bearing, not decoration. */
  const viewModeStyle = isViewMode
    ? { pointerEvents: "none", opacity: 0.6, userSelect: "none" }
    : {};

  /* Both the X and Cancel resolve to this: close the local modal AND release the
     router blocker, or the next navigation attempt finds a blocker still blocked. */
  const dismissUnsavedModal = () => {
    setShowSaveModal(false);
    if (blocker.state === "blocked") blocker.reset();
  };

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-edge tw-mb-4">
        {/* § 263 — `tw-min-w-0` so this group can shrink below its content width. A
            flex item defaults to `min-width: auto`, which refuses to shrink, so a long
            rule name would shove the Commit/Save/Decommit cluster instead of
            ellipsising. With it, the title below truncates and the buttons stay put. */}
          {/* § 285a — the title is CAPPED, not just truncated. `min-w-0` lets this group
              shrink; without a max it also GROWS into every pixel the action cluster
              does not use, so at 24px the name ran right up against the status chip and
              the header read as one jammed line. 32rem is ~38-40 characters at 24/600 —
              past the point where a longer prefix stops telling you which rule this is —
              and the Tooltip carries the full name. The same cap is used by the other two
              record-name headers (CS config, Product Cards editor) so they agree. */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-pb-2 tw-min-w-0">
          {/* Was a bare `<button className="tw-float-left tw-w-[4%]">`: `float` does
              nothing to a flex item, and `w-[4%]` sized the control off the header's
              width rather than off its own glyph. */}
          <Button
            variant="subtle"
            size="sm"
            aria-label="Back"
            data-testid="rule-back-btn"
            onClick={onBackButtonClicked}
          >
            <ChevronLeft size={20} />
          </Button>
          {/*
            `as="h1"`, where all three branches were a `<label>` — the same defect
            § 108 fixed on the list page. A `<label>` that labels nothing gives the
            page no heading at all: nothing for a screen reader to jump to and
            nothing in the document outline. The route below `/gms/compatibilities`
            now has the `h1` its parent got.
          */}
          {fromCategoryRelevancy ? (
            <Typography
              variant="display"
              as="h1"
              className="tw-text-ink tw-truncate tw-max-w-[32rem]"
              data-testid="rule-page-title"
            >
              Create Compatibility
            </Typography>
          ) : ruleHeaderDetails?.name !== "" ? (
            /* § 263 — was `tw-w-32`: a hard-coded 128px box in a header whose middle
               is empty, so even a short name was clipped. It now sizes to its content
               and shrinks when the row runs out of room, which is when `truncate`
               earns its ellipsis. The 128px box is also what turned the § 243
               balance/truncate conflict into FOUR lines rather than two. */
            <div className="tw-min-w-0">
              <Tooltip content={ruleHeaderDetails?.name} usePortal block>
                <Typography
                  variant="display"
                  as="h1"
                  className="tw-text-ink tw-truncate tw-block tw-max-w-[32rem]"
                  data-testid="rule-page-title"
                >
                  {ruleHeaderDetails?.name}
                </Typography>
              </Tooltip>
            </div>
          ) : (
            <Typography
              variant="display"
              as="h1"
              className="tw-text-ink tw-truncate tw-max-w-[32rem]"
              data-testid="rule-page-title"
            >
              Compatibility
            </Typography>
          )}
          {editPage ? statusChip() : null}
        </div>

        <div
          className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-pb-2"
          style={viewModeStyle}
        >
          {editPage ? (
            <>
              {/*
                § 66's quiet chip: "Edge Count 412" DESCRIBES the rule, it is not a
                value anyone selected, so it wears `meta` rather than the selection
                colours. Replaces a `tw-bg-[#dde0fc]` literal that stayed pale
                indigo under a dark theme with `ink` text on top of it.
              */}
              <Tag
                variant="meta"
                label={`Edge Count ${edgeInfo?.count ?? 0}`}
                data-testid="rule-edge-count"
              />
              <ReloadButton
                loading={edgeInfo.status === "Loading"}
                onClick={() => {
                  getRuleStatus();
                  getEdgeCount();
                }}
              />
            </>
          ) : null}
          <>
            {editPage ? (
              /*
                `secondary`, not `danger`. Decommit IS destructive and the design
                system has an outline-danger variant for exactly "a destructive
                action beside a primary" — but the legacy control is a blue outline,
                this screen was not in the brief, and turning it red is a visual
                decision nobody asked for. Noted in `okf/rules/e2e.md` as the one
                deliberate hold-back in this migration.
              */
              <Button
                variant="secondary"
                data-testid="rule-decommit-btn"
                disabled={disableButton.decommit}
                onClick={() => {
                  if (appData.pm.checkPermission("gmsrules_update")) {
                    onHandleDecommit();
                  } else {
                    toast.info(
                      "You don't have permission to decommit the rules"
                    );
                  }
                }}
              >
                Decommit
              </Button>
            ) : (
              <Button
                variant="secondary"
                data-testid="rule-cancel-btn"
                onClick={() => {
                  if (fromCategoryRelevancy) {
                    onClose?.();
                  } else {
                    navigate("/gms/compatibilities");
                  }
                }}
              >
                Cancel
              </Button>
            )}
            {/* Two primaries side by side is the legacy arrangement (two
                `button-fill`s) and is preserved. The design system would rather
                Commit were the only fill, since a second one competes for "the"
                action — also recorded as a hold-back rather than changed here. */}
            {(type === 'copy' ? true : !ruleDetails?.commit) && (
              <Button
                variant="primary"
                data-testid="rule-save-btn"
                disabled={disableButton.save}
                onClick={() => {
                  if (appData.pm.checkPermission("gmsrules_update")) {
                    saveRule(true);
                  } else {
                    toast.info("You don't have permission to save the rules");
                  }
                }}
              >
                Save
              </Button>
            )}
            {editPage && (
              <Button
                variant="primary"
                data-testid="rule-commit-btn"
                disabled={disableButton?.commit}
                onClick={() => {
                  if (appData.pm.checkPermission("gmsrules_update")) {
                    handleCommit();
                  } else {
                    toast.info("You don't have permission to commit the rules");
                  }
                }}
              >
                Commit
              </Button>
            )}
          </>
        </div>
      </div>
      <div style={viewModeStyle}>
        {/*
          `items-start`, not `items-center`, plus a 36px band around the switch.

          Every field is now label-above-control, and the Asymmetric block is shorter
          than the other two (a 24px switch against a 36px input). Centring the BLOCKS
          therefore dropped its label below the other two — measured 5px in the legacy
          layout. Top-aligning the blocks puts the three labels on one edge, and the
          `tw-h-9` wrapper below re-centres the 24px switch inside the same 36px
          control band the input and the dropdown occupy.

          Measured in headless Chromium against the built stylesheet, 2026-08-27:
          label tops 24 / 24 / 24 and control centres 70 / 70 / 70 — 0px spread on
          both. Getting to 0 also required a design-system fix: `FieldTitle`'s
          required asterisk had no type token, so it inherited a 21.6px line box
          against the label's 20px and pushed this column's control down 1.61px. See
          the note in `FieldTitle.tsx`.
        */}
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className="tw-w-[30%]">
            {/*
              `aria-describedby="nameHelp"` is gone with the raw input. There is no
              `#nameHelp` on this route — the id exists in Attribute, Category and
              Builder, never here — so the attribute pointed at nothing, which is
              worse than absent: a screen reader is told a description exists and
              then finds none. What it was reaching for is now the field's own
              `infoMessage` tooltip, and `required` reaches the control as
              `aria-required` instead of only drawing an asterisk.
            */}
            <TextField
              label={RULE_NAME}
              required
              infoMessage={RULE_NAME_INSTRUCTIONS}
              id="name"
              data-testid="components-ruleheader-input-1"
              value={ruleHeaderDetails.name}
              placeholder="Rule Name"
              maxLength={60}
              minLength={2}
              onChange={(value) => handle_change("name", value)}
            />
          </div>
          <div className="tw-w-[20%]">
            {/*
              The `<option value="">Select Rule Type</option>` is NOT carried over;
              `placeholder` renders the same words in the same place without being
              selectable. That is a deliberate narrowing: choosing it called
              `handle_change('rule_type', '')`, which wipes `rule_body`, clears every
              CBM option and leaves a form that cannot be saved (Save is gated on a
              truthy `rule_type`). It was a `<select>` idiom, not a feature — no code
              path or test ever set the value back to empty. Re-adding it is one
              option entry if that turns out to be wrong.
            */}
            <Dropdown
              label={RULE_TYPE}
              infoMessage={
                <>
                  <p>{CBA_INSTRUCTIONS}</p>
                  <p>{CBI_INSTRUCTIONS}</p>
                  <p>{CBM_INSTRUCTIONS}</p>
                </>
              }
              id="rule_type"
              data-testid="components-ruleheader-select-2"
              placeholder={SELECT_RULE_TYPE}
              options={[
                { value: CBI_RULE, label: CBI_RULE },
                { value: CBA_RULE, label: CBA_RULE },
                { value: CBM_RULE, label: CBM_RULE },
              ]}
              value={ruleHeaderDetails.rule_type ?? ''}
              disabled={type === 'copy' ? false : Boolean(ruleDetails?.commit)}
              onChange={handleOnRuleTypeChange}
            />
          </div>
          <div className="tw-w-[10%]">
            <FieldTitle
              label={ASYMMETRIC}
              infoMessage={ASYMMETRIC_INSTRUCTIONS}
              className="tw-mb-2"
            />
            <div className="tw-h-9 tw-flex tw-items-center">
              {/*
                `label=""` + `aria-label`: the visible label is the `FieldTitle`
                above, which a switch cannot claim with `htmlFor` (a `<button>` is
                not labelable). The legacy `ToggleButton` had **no accessible name at
                all** — its `<label>` wrapped the input but contained no text, so the
                control announced as an unlabelled checkbox.
              */}
              <Toggle
                label=""
                aria-label={ASYMMETRIC}
                checked={Boolean(ruleHeaderDetails.asymmetric)}
                onChange={handleOnAsymmetricChange}
                data-testid="rule-asymmetric-toggle"
              />
            </div>
          </div>
        </div>
        <div className="tw-mt-4 tw-flex tw-items-center tw-gap-4 tw-mb-4">
          <div className="tw-w-[51%]">
            {/* The raw textarea carried `type="text"`, which is not an attribute a
                `<textarea>` has. Dropped, not translated. */}
            <Textarea
              label={RULE_DESCRIPTION}
              id="description"
              data-testid="components-ruleheader-textarea-3"
              placeholder={RULE_DESCRIPTION}
              value={ruleHeaderDetails.description}
              rows={3}
              onChange={(value) => handle_change("description", value)}
            />
          </div>
        </div>
        {/* Was `Bootbox.confirm("Change Rule Type?", …)` — a global jQuery plugin fed
            an HTML string, with `button-outlined`/`button-fill` on its buttons. See
            `RuleChangeConfirmDialog` for what moved and what did not. */}
        <RuleChangeConfirmDialog
          isOpen={pendingRuleType !== null}
          title="Change Rule Type?"
          message={CHANGE_RULE_WARNING}
          onConfirm={confirmRuleTypeChange}
          onCancel={() => setPendingRuleType(null)}
          data-testid="rule-type-change-confirm"
        />
        {showDecommitModal ? (
          <DecommitConfirmModal
            title="Decommit"
            message="you will lose all data here"
            onClose={() => setShowDecommitModal(false)}
            onConfirm={onDecommitConfirm}
            ternaryMethod={onDecommitConfirm}
            ternaryText="text"
            confirmText="Decommit"
            cancelText="Cancel"
          />
        ) : null}
        {/*
          The Unsaved Changes confirm, on the DS `Dialog` (§ 112).

          It was `ConfirmModal` (`ISR/Components/RuleComponents/CommitConfirmModal`),
          which stays exactly where it is for its five other consumers — only this
          call site moves. Three things that were wrong with it here:

          - **`id` is a required prop and was never passed.** The component templates
            five DOM ids off it, so this modal shipped `id="undefined-content"`,
            `"undefined-title"`, `"undefined-close-btn"`, `"undefined-ternary-btn"`
            and `"undefined-actions"`. `RuleHeader.js` is a `.js` file, so nothing
            type-checked the call.
          - **No `role="dialog"`, no `aria-modal`, no accessible name**, no focus
            move on open, no focus restore on close, no body scroll lock, and Escape
            did nothing. `Dialog` brings all of it.
          - It rendered inside the `viewModeStyle` wrapper, so in `view` mode it
            would have been drawn at 60% opacity with pointer events off. `Dialog`
            portals to `document.body` and escapes that. (Unreachable in view mode
            either way — `hasUnsavedChanges` is gated on `!isViewMode`.)

          Behaviour preserved: the three actions and their handlers, and the fact
          that Cancel and X are the same operation. Escape and a backdrop click now
          also resolve to that operation, where the legacy backdrop swallowed clicks
          — both are "stay on the page", which is the safe direction.
        */}
        {showSaveModal || blocker.state === "blocked" ? (
          <Dialog
            isOpen
            /*
              `md`, not `sm` (§ 113). Reported as "Discard / changes" and "Save /
              Changes" each broken across two lines.

              Measured against the built stylesheet: at their natural widths the three
              buttons need **363px** (144 + 78 + 125, plus two 8px gaps). `sm` is
              `max-w-sm` = 384px, and the footer's own `tw-px-6` takes 48 of that,
              leaving **336px** — 27px short. So the row had to give somewhere, and
              a flex item that cannot shrink further wraps its text.

              `md` (490px, the Figma default) leaves 442px, which is 79px of slack —
              enough to absorb a font wider than the one measured. TWO buttons fit
              `sm` comfortably; three do not. `whitespace-nowrap` below is the actual
              guarantee, the width is what stops the guarantee costing a scrollbar —
              the same pairing as § 111's "View Log" column.
            */
            size="md"
            title="Unsaved Changes"
            onClose={dismissUnsavedModal}
            data-testid="rule-unsaved-dialog"
            footer={
              /*
                SPLIT footer: the destructive action alone on the left, the two safe
                ones grouped right (§ 114).

                Three buttons in one right-aligned row put "Discard changes"
                immediately beside "Cancel" — 8px between an action that throws work
                away and the action that means "never mind". Separating them is the
                standard treatment for a ternary dialog action, and it is a real
                safeguard, not just tidiness: a click that misses Cancel to the left
                now lands on empty footer, not on Discard.

                DOM ORDER IS UNCHANGED — Discard, Cancel, Save — so tab order and the
                footer-order test still read left to right exactly as before. Only the
                visual grouping moves, via `justify-between` plus a wrapper around the
                right-hand pair.
              */
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <Button
                  variant="danger"
                  className="tw-whitespace-nowrap"
                  data-testid="rule-unsaved-discard-btn"
                  onClick={onDiscardChanges}
                >
                  Discard changes
                </Button>
                <div
                  className="tw-flex tw-items-center tw-gap-2"
                  data-testid="rule-unsaved-primary-actions"
                >
                  <Button
                    variant="secondary"
                    className="tw-whitespace-nowrap"
                    data-testid="rule-unsaved-cancel-btn"
                    onClick={dismissUnsavedModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="tw-whitespace-nowrap"
                    data-testid="rule-unsaved-save-btn"
                    onClick={onSaveConfirm}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            }
          >
            {/* The yellow panel was `tw-bg-[#fffae6] tw-border-[#ffc400]` plus a
                `YellowWarningIcon` — a hand-built warning banner, which is what
                `Alert tone="warning"` is. Same two colours, from tokens, themed. */}
            <Alert tone="warning" data-testid="rule-unsaved-note">
              You have unsaved changes to this Compatibility. What would you like to do?
            </Alert>
          </Dialog>
        ) : null}
      </div>
    </>
  );
};
export default RuleHeader;
